import { createTheme } from '@mui/material/styles';

export const tavernTokens = {
  green: '#0F4C45',
  greenDark: '#0B3A35',
  greenBlack: '#062522',
  gold: '#D7A04B',
  goldDark: '#9D6A25',
  goldSoft: '#F1D89B',
  parchment: '#F5F1E6',
  parchmentLight: '#FFFAEF',
  parchmentDeep: '#E9DDC4',
  ink: '#1F251F',
  mutedInk: '#66746D',
  border: '#D8C7A4',
  ruby: '#A53A2A',
  success: '#2F8A56',
  warning: '#C27A22',
  error: '#A83A32',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tavernTokens.green,
      dark: tavernTokens.greenDark,
      light: '#1B6D63',
      contrastText: '#FFF8E6',
    },
    secondary: {
      main: tavernTokens.gold,
      dark: tavernTokens.goldDark,
      light: tavernTokens.goldSoft,
      contrastText: tavernTokens.greenBlack,
    },
    success: {
      main: tavernTokens.success,
    },
    warning: {
      main: tavernTokens.warning,
    },
    error: {
      main: tavernTokens.error,
    },
    background: {
      default: tavernTokens.parchment,
      paper: tavernTokens.parchmentLight,
    },
    text: {
      primary: tavernTokens.ink,
      secondary: tavernTokens.mutedInk,
    },
    divider: tavernTokens.border,
    action: {
      disabled: '#8D948E',
      disabledBackground: '#E8E1D2',
    },
    tavern: tavernTokens,
  },
  shadows: [
    'none',
    '0 1px 0 rgba(6, 37, 34, 0.04), 0 8px 24px -14px rgba(6, 37, 34, 0.24)',
    '0 10px 30px -18px rgba(6, 37, 34, 0.32)',
    '0 16px 40px -22px rgba(6, 37, 34, 0.42)',
    '0 20px 48px -24px rgba(6, 37, 34, 0.48)',
    ...Array(20).fill('0 20px 48px -24px rgba(6, 37, 34, 0.48)'),
  ],
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily:
      '"Inter", "Roboto", "Helvetica", "Arial", system-ui, sans-serif',
    h1: {
      fontFamily: '"Cinzel", "Cormorant Garamond", Georgia, serif',
      fontSize: 'clamp(2rem, 4vw, 3.75rem)',
      lineHeight: 1.05,
      fontWeight: 800,
    },
    h2: {
      fontFamily: '"Cinzel", "Cormorant Garamond", Georgia, serif',
      fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)',
      lineHeight: 1.15,
      fontWeight: 800,
    },
    h3: {
      fontFamily: '"Cinzel", "Cormorant Garamond", Georgia, serif',
      fontSize: '1.25rem',
      lineHeight: 1.2,
      fontWeight: 800,
    },
    h4: {
      fontFamily: '"Cinzel", "Cormorant Garamond", Georgia, serif',
      fontSize: '1rem',
      lineHeight: 1.25,
      fontWeight: 800,
    },
    subtitle2: {
      fontSize: '0.7rem',
      fontWeight: 800,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
    },
    button: {
      fontWeight: 800,
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        },
        'html.tavern-modal-open': {
          overflow: 'hidden',
          overscrollBehavior: 'none',
        },
        'body.tavern-modal-open': {
          overflow: 'hidden',
          overscrollBehavior: 'none',
        },
        '.MuiModal-root': {
          overscrollBehavior: 'contain',
        },
        '.MuiDialog-container, .MuiDrawer-root .MuiPaper-root': {
          overscrollBehavior: 'contain',
        },
        '.MuiDialog-paper, .MuiDialogContent-root, .MuiDrawer-paper': {
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
        },
        body: {
          background:
            'radial-gradient(circle at 12% 8%, rgba(215,160,75,0.2), transparent 26rem), radial-gradient(circle at 88% 78%, rgba(15,76,69,0.12), transparent 34rem)',
        },
        '::selection': {
          backgroundColor: tavernTokens.gold,
          color: tavernTokens.greenBlack,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:599.95px)': {
            paddingLeft: 'max(20px, calc(env(safe-area-inset-left, 0px) + 20px))',
            paddingRight: 'max(20px, calc(env(safe-area-inset-right, 0px) + 20px))',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          minHeight: 40,
          '&.Mui-disabled': {
            background: '#E8E1D2',
            borderColor: '#D8C7A4',
            color: '#8D948E',
            boxShadow: 'none',
          },
          '&:focus-visible': {
            outline: `3px solid ${tavernTokens.goldSoft}`,
            outlineOffset: 2,
          },
        },
        containedPrimary: {
          background:
            'linear-gradient(135deg, #0F4C45 0%, #0B3A35 100%)',
          boxShadow: '0 14px 26px -18px rgba(6, 37, 34, 0.82)',
          '&:hover': {
            background:
              'linear-gradient(135deg, #176D62 0%, #0B3A35 100%)',
          },
        },
        containedSecondary: {
          background:
            'linear-gradient(135deg, #F1D89B 0%, #D7A04B 100%)',
          color: tavernTokens.greenBlack,
          boxShadow: '0 14px 24px -18px rgba(157, 106, 37, 0.7)',
        },
        outlinedPrimary: {
          borderColor: 'rgba(15, 76, 69, 0.34)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          borderColor: tavernTokens.border,
          backgroundImage:
            'linear-gradient(180deg, rgba(255,250,239,0.98), rgba(245,241,230,0.92))',
          boxShadow:
            '0 1px 0 rgba(6, 37, 34, 0.04), 0 8px 24px -14px rgba(6, 37, 34, 0.24)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${tavernTokens.border}`,
          boxShadow: '0 24px 64px -30px rgba(6, 37, 34, 0.5)',
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        root: {
          position: 'relative',
          alignItems: 'flex-start',
          borderRadius: 8,
          border: `1px solid ${tavernTokens.border}`,
          borderLeftWidth: 5,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(245,241,230,0.94) 100%)',
          boxShadow:
            '0 1px 0 rgba(6, 37, 34, 0.04), 0 14px 34px -26px rgba(6, 37, 34, 0.45)',
          color: tavernTokens.ink,
          fontWeight: 650,
          letterSpacing: 0,
          padding: '10px 14px',
          '& .MuiAlert-icon': {
            marginRight: 10,
            padding: '2px 0',
            opacity: 1,
          },
          '& .MuiAlert-message': {
            minWidth: 0,
            padding: '2px 0',
          },
          '& .MuiAlert-action': {
            alignItems: 'center',
            paddingTop: 0,
          },
          '& a': {
            color: tavernTokens.green,
            fontWeight: 800,
          },
        },
        standardSuccess: {
          borderColor: 'rgba(47, 138, 86, 0.42)',
          borderLeftColor: tavernTokens.success,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(235,246,235,0.86) 100%)',
          '& .MuiAlert-icon': {
            color: tavernTokens.success,
          },
        },
        standardError: {
          borderColor: 'rgba(168, 58, 50, 0.38)',
          borderLeftColor: tavernTokens.error,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(250,233,226,0.88) 100%)',
          '& .MuiAlert-icon': {
            color: tavernTokens.error,
          },
        },
        standardWarning: {
          borderColor: 'rgba(194, 122, 34, 0.42)',
          borderLeftColor: tavernTokens.warning,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(252,238,203,0.9) 100%)',
          '& .MuiAlert-icon': {
            color: tavernTokens.warning,
          },
        },
        standardInfo: {
          borderColor: 'rgba(15, 76, 69, 0.32)',
          borderLeftColor: tavernTokens.green,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(232,242,237,0.9) 100%)',
          '& .MuiAlert-icon': {
            color: tavernTokens.green,
          },
        },
        outlinedSuccess: {
          borderColor: 'rgba(47, 138, 86, 0.42)',
          borderLeftColor: tavernTokens.success,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(235,246,235,0.86) 100%)',
        },
        outlinedError: {
          borderColor: 'rgba(168, 58, 50, 0.38)',
          borderLeftColor: tavernTokens.error,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(250,233,226,0.88) 100%)',
        },
        outlinedWarning: {
          borderColor: 'rgba(194, 122, 34, 0.42)',
          borderLeftColor: tavernTokens.warning,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(252,238,203,0.9) 100%)',
        },
        outlinedInfo: {
          borderColor: 'rgba(15, 76, 69, 0.32)',
          borderLeftColor: tavernTokens.green,
          backgroundImage:
            'linear-gradient(135deg, rgba(255,250,239,0.98) 0%, rgba(232,242,237,0.9) 100%)',
        },
        filledSuccess: {
          borderColor: 'rgba(47, 138, 86, 0.5)',
          borderLeftColor: tavernTokens.gold,
          background: `linear-gradient(135deg, ${tavernTokens.green} 0%, ${tavernTokens.greenDark} 100%)`,
          color: tavernTokens.parchmentLight,
        },
        filledError: {
          borderColor: 'rgba(168, 58, 50, 0.5)',
          borderLeftColor: tavernTokens.gold,
          background: `linear-gradient(135deg, ${tavernTokens.ruby} 0%, #7F2C25 100%)`,
          color: tavernTokens.parchmentLight,
        },
        filledWarning: {
          borderColor: 'rgba(194, 122, 34, 0.55)',
          borderLeftColor: tavernTokens.green,
          background: `linear-gradient(135deg, ${tavernTokens.goldSoft} 0%, ${tavernTokens.gold} 100%)`,
          color: tavernTokens.greenBlack,
        },
        filledInfo: {
          borderColor: 'rgba(15, 76, 69, 0.42)',
          borderLeftColor: tavernTokens.gold,
          background: `linear-gradient(135deg, ${tavernTokens.parchmentLight} 0%, ${tavernTokens.parchmentDeep} 100%)`,
          color: tavernTokens.ink,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'rgba(255, 250, 239, 0.76)',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tavernTokens.green,
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: tavernTokens.border,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 800,
        },
      },
    },
  },
});

export default theme;
